import { useSearch } from "@redotech/react-util/search";
import RedoIconArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import SearchSm from "@redotech/redo-icons/search-sm.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import classNames from "classnames";
import Fuse from "fuse.js";
import {
  JSXElementConstructor,
  memo,
  useContext,
  useMemo,
  useState,
} from "react";
import { CommandPaletteOpenContext } from "../../command-palette/contexts/command-palette-context";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { RedoIcon } from "../icon/redo-icon";
import { RedoTextInput } from "../input/redo-text-input";
import { UserProfile } from "./common/redo-nav-user-profile";
import * as navigationCss from "./redo-navigation.module.css";
import {
  RedoSecondaryNavItem,
  SecondaryNavItemProps,
  useNavTreeNode,
} from "./secondary/redo-secondary-nav-item";

export interface SettingItem {
  href: string;
  friendlyName: string;
  id: string;
  UnfilledIcon?: JSXElementConstructor<any>;
  FilledIcon?: JSXElementConstructor<any>;
}

export enum SettingsSectionEnum {
  GENERAL = "general",
  PERSONAL = "personal",
  AI = "ai",
  // CATALOG = "catalog",
  CONVERSION = "conversion",
  CUSTOMER_ACCOUNTS = "customer-accounts",
  MARKETING = "marketing",
  ORDER_TRACKING = "order-tracking",
  RETURNS_AND_CLAIMS = "returns-and-claims",
  PRODUCTS = "products",
  WARRANTIES = "warranties",
  INVENTORY_MANAGEMENT = "inventory-management",
  ORDER_FULFILLMENT = "order-fulfillment",
  SHIPPING = "shipping",
  INTERNATIONAL = "international",
  SUPPORT = "support",
  RECOVER = "recover",
}

export const settingsSectionToFriendly: Record<SettingsSectionEnum, string> = {
  [SettingsSectionEnum.GENERAL]: "General",
  [SettingsSectionEnum.PERSONAL]: "Personal",
  [SettingsSectionEnum.AI]: "AI",
  // [SettingsSectionEnum.CATALOG]: "Catalog",
  [SettingsSectionEnum.CONVERSION]: "Conversion",
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: "Customer accounts",
  [SettingsSectionEnum.MARKETING]: "Marketing",
  [SettingsSectionEnum.ORDER_TRACKING]: "Order tracking",
  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: "Returns & claims",
  [SettingsSectionEnum.PRODUCTS]: "Products",
  [SettingsSectionEnum.WARRANTIES]: "Warranties",
  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: "Inventory management",
  [SettingsSectionEnum.ORDER_FULFILLMENT]: "Order Management",
  [SettingsSectionEnum.SHIPPING]: "Shipping",
  [SettingsSectionEnum.INTERNATIONAL]: "International",
  [SettingsSectionEnum.SUPPORT]: "Support",
  [SettingsSectionEnum.RECOVER]: "Recover",
};

export interface SettingsSection {
  name: SettingsSectionEnum;
  items: SettingItem[];
}

export const RedoSettingsNav = memo(function RedoSettingsNav({
  sections,
  exitSettingsUrl,
  hidden = false,
}: {
  sections: SettingsSection[];
  exitSettingsUrl?: string;
  /**
   * When true, renders items invisibly for tree registration (global search).
   * Only renders when command palette is open.
   */
  hidden?: boolean;
}) {
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  // Register "Settings" as parent node for breadcrumbs (Settings > General > Billing)
  const settingsNavItem = useMemo(
    () => ({ friendlyName: "Settings", Icon: SettingsIcon, url: undefined }),
    [],
  );
  const { id: settingsNodeId, TreeContext } = useNavTreeNode(settingsNavItem);

  // When hidden, only render for tree registration when command palette is open
  if (hidden) {
    if (!isCommandPaletteOpen) {
      return null;
    }
    return (
      <TreeContext.Provider value={settingsNodeId}>
        {sections.map((section) => (
          <SettingsSectionComponent
            hidden
            items={section.items}
            key={section.name}
            name={section.name}
          />
        ))}
      </TreeContext.Provider>
    );
  }

  return (
    <TreeContext.Provider value={settingsNodeId}>
      <VisibleSettingsNav
        exitSettingsUrl={exitSettingsUrl!}
        sections={sections}
      />
    </TreeContext.Provider>
  );
});

/**
 * The visible settings navigation with full UI (back button, search, user profile).
 */
interface SearchableSettingItem extends SettingItem {
  sectionName: string;
}

const VisibleSettingsNav = memo(function VisibleSettingsNav({
  sections,
  exitSettingsUrl,
}: {
  sections: SettingsSection[];
  exitSettingsUrl: string;
}) {
  const [search, setSearch] = useState("");

  const flattenedItems = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          sectionName: settingsSectionToFriendly[section.name],
        })),
      ),
    [sections],
  );

  const itemSearcher = useMemo(() => {
    const searcher = new Fuse<SearchableSettingItem>([], {
      keys: ["friendlyName", "sectionName"],
      threshold: 0.4,
    });
    return searcher;
  }, []);

  const searchedItems = useSearch(itemSearcher, flattenedItems, search);

  const modifiedSections = useMemo(() => {
    const searchItemsInResults = new Set(searchedItems.map((item) => item.id));

    const modifiedSections = sections.map((section) => {
      const items = section.items.filter((item) =>
        searchItemsInResults.has(item.id),
      );
      return { name: section.name, items };
    });

    const nonEmptySections = modifiedSections.filter(
      (section) => section.items.length > 0,
    );

    return nonEmptySections;
  }, [searchedItems, sections]);

  return (
    <Flex
      bgColor="primary"
      className={classNames(navigationCss.settingsNav, darkModeCss.darkMode)}
      dir="column"
      grow={1}
      justify="space-between"
    >
      <Flex dir="column" gap="xl" overflow="hidden" py="3xl">
        <Flex px="sm">
          <Flex
            align="center"
            as="a"
            className={navigationCss.backButton}
            grow={1}
            linkProps={{ to: exitSettingsUrl }}
            px="3xl"
            py="sm"
            radius="sm"
          >
            <RedoIcon color="tertiary" Icon={RedoIconArrowLeft} size={16} />{" "}
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              Back
            </Text>
          </Flex>
        </Flex>
        <Flex px="3xl">
          <RedoTextInput
            autoFocus
            IconLeading={SearchSm}
            placeholder="Search"
            setValue={setSearch}
            value={search}
          />
        </Flex>
        <Flex
          className={navigationCss.settingsNavItems}
          dir="column"
          gap="lg"
          px="xl"
        >
          {modifiedSections.map((section) => (
            <SettingsSectionComponent
              items={section.items}
              key={section.name}
              name={section.name}
            />
          ))}
        </Flex>
      </Flex>
      <UserProfile collapsed={false} />
    </Flex>
  );
});

function settingItemToProps(item: SettingItem): SecondaryNavItemProps {
  const base = {
    action: { type: "link" as const, href: item.href },
    id: item.id,
    friendlyName: item.friendlyName,
  };
  if (item.UnfilledIcon && item.FilledIcon) {
    return {
      ...base,
      UnfilledIcon: item.UnfilledIcon,
      FilledIcon: item.FilledIcon,
    };
  }
  return base;
}

/**
 * Renders a settings section with its items.
 * When hidden, registers with tree context for global search with proper breadcrumbs.
 */
const SettingsSectionComponent = memo(function SettingsSectionComponent({
  name,
  items,
  hidden = false,
}: SettingsSection & { hidden?: boolean }) {
  // Register section as parent node for breadcrumbs (e.g., "General", "AI")
  const sectionNavItem = useMemo(
    () => ({
      friendlyName: settingsSectionToFriendly[name],
      Icon: undefined,
      url: undefined,
    }),
    [name],
  );
  const { id: sectionNodeId, TreeContext } = useNavTreeNode(sectionNavItem);

  if (hidden) {
    return (
      <TreeContext.Provider value={sectionNodeId}>
        {items.map((item) => (
          <RedoSecondaryNavItem
            {...settingItemToProps(item)}
            hidden
            key={item.id}
          />
        ))}
      </TreeContext.Provider>
    );
  }

  return (
    <TreeContext.Provider value={sectionNodeId}>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
          {settingsSectionToFriendly[name]}
        </Text>
        <Flex dir="column" gap="xs">
          {items.map((item) => (
            <RedoSecondaryNavItem {...settingItemToProps(item)} key={item.id} />
          ))}
        </Flex>
      </Flex>
    </TreeContext.Provider>
  );
});
